/**
 * useKeywordTabsLogic - Custom hook for KeywordTabs business logic
 * Extracted from KeywordTabs to centralize all business logic
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSimpleKeywordQuery } from '@/modules/app/hooks/useSimpleKeywordQuery';
import { useAiAnalysisWebhook } from '@/modules/app/hooks';
import { debugUserId, getSafeUserId } from '@/modules/app/utils/userIdDebug';
import { debugLog } from '@/lib/debugUtils';
import { toast } from 'sonner';
import { TAB_CONFIG } from '../config/tabConfig';
import { KeywordTabsProps } from '../types/keywordTabsTypes';

interface NotificationState {
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string | React.ReactNode;
}

export const useKeywordTabsLogic = ({
  currentQuery,
  currentLanguageName,
  currentCountryName,
  userId,
  workspaceId,
  workspaceName,
  strictWorkspaceFiltering = false,
}: KeywordTabsProps) => {
  const [activeTab, setActiveTab] = useState('keywords');
  const [notification, setNotification] = useState<NotificationState | null>(null);

  const { user } = useAuth();
  const { data, isLoading, error, startRealtime, stopRealtime, clearResults, connectionStatus } = useSimpleKeywordQuery();

  // AI Analysis Hook
  const {
    triggerAiAnalysis,
    isLoading: isAiAnalysisLoading,
    error: aiAnalysisError,
    getErrorMessage: getAiErrorMessage,
    clearError: clearAiError
  } = useAiAnalysisWebhook();

  // Enhanced user ID validation and debugging
  const validatedUserId = useMemo(() => {
    if (!userId) {
      debugUserId('null', 'KeywordTabs.validatedUserId');
      return null;
    }
    
    // Debug the received user ID
    debugUserId(userId, 'KeywordTabs.received');
    
    // Get safe user ID for database operations
    const safeUserId = getSafeUserId(userId, 'KeywordTabs.validation');
    
    if (safeUserId !== userId) {
      debugLog(user?.primaryEmailAddress?.emailAddress, '[KeywordTabs] User ID validation changed:', {
        original: userId,
        safe: safeUserId,
        willPreventSearch: safeUserId === null
      });
    }
    
    return safeUserId;
  }, [userId, user]);

  // FIX: Stabilized search parameters using refs to prevent constant changes
  const searchParamsRef = useRef<Record<string, unknown> | null>(null);
  const lastQueryParamsRef = useRef<string>('');

  // Create stable search params only when needed values actually change
  const getSearchParams = useCallback(() => {
    const currentParamsKey = JSON.stringify({
      query: currentQuery,
      language: currentLanguageName,
      location: currentCountryName,
      dataType: activeTab,
      userId: validatedUserId,
      workspaceId,
      strictWorkspaceFiltering,
    });

    // Only update if params actually changed
    if (lastQueryParamsRef.current !== currentParamsKey) {
      lastQueryParamsRef.current = currentParamsKey;
      searchParamsRef.current = {
        query: currentQuery,
        language: currentLanguageName,
        location: currentCountryName,
        dataType: activeTab,
        userId: validatedUserId || '',
        workspaceId: workspaceId, // Include workspace ID for proper data isolation
        strictWorkspaceFiltering: strictWorkspaceFiltering,
      };
      
      // Debug the final search parameters only when they change
      debugLog(user?.primaryEmailAddress?.emailAddress, '[KeywordTabs] Search parameters updated:', searchParamsRef.current);
    }
    
    return searchParamsRef.current;
  }, [currentQuery, currentLanguageName, currentCountryName, activeTab, validatedUserId, workspaceId, strictWorkspaceFiltering, user]);

  // Enhanced tab change handler with workspace context logging
  const handleTabChange = useCallback((newTab: string) => {
    if (newTab === activeTab) return; // No change needed
    
    debugLog(user?.primaryEmailAddress?.emailAddress, `[KeywordTabs] Tab changed from ${activeTab} to ${newTab}${workspaceId ? ` in workspace ${workspaceId}` : ''}`);
    setActiveTab(newTab);
  }, [activeTab, workspaceId, user]);

  // Enhanced AI Analysis action - copied and adapted from WorkspaceDetailPage
  const handleAiAnalysis = useCallback(async () => {
    const userEmail = user?.primaryEmailAddress?.emailAddress;
    debugLog(userEmail, '🛠 =====================================');
    debugLog(userEmail, '🛠 AI ANALYSIS BUTTON CLICKED');
    debugLog(userEmail, '🛠 =====================================');
    
    // Step 1: Environment Check
    const webhookUrl = import.meta.env.VITE_AI_ANALYSIS_WEBHOOK;
    debugLog(userEmail, '🔍 Environment Check:', {
      webhookUrl: webhookUrl || 'NOT SET',
      hasWebhookUrl: !!webhookUrl,
      mode: import.meta.env.MODE,
      timestamp: new Date().toISOString()
    });

    // Step 2: Basic State Check
    debugLog(userEmail, '🔍 Component State:', {
      workspaceId,
      workspaceName: workspaceName,
      currentQuery,
      dataLength: data?.length || 0,
      validatedUserId: validatedUserId ? 'SET' : 'NOT SET',
      isAiAnalysisLoading
    });

    // Step 3: Button State Check
    const isButtonDisabled = isAiAnalysisLoading || !data || data.length === 0;
    debugLog(userEmail, '🔍 Button State:', {
      isDisabled: isButtonDisabled,
      reason: isAiAnalysisLoading ? 'Loading' : !data || data.length === 0 ? 'No Keywords' : 'Enabled'
    });

    // Step 4: Early Exit Checks
    if (!workspaceId) {
      debugLog(userEmail, '❌ Missing workspace ID');
      toast.error('Workspace not available for analysis');
      return;
    }

    if (!data || data.length === 0) {
      debugLog(userEmail, '❌ No keyword data available');
      toast.error('No keyword data found. Please run a keyword search first.');
      return;
    }

    if (!validatedUserId) {
      debugLog(userEmail, '❌ Authentication issue');
      toast.error('Please log in to use AI analysis');
      return;
    }

    if (!webhookUrl) {
      debugLog(userEmail, '❌ Missing webhook URL');
      toast.error('AI analysis service not configured. Please check environment variables.');
      return;
    }

    debugLog(userEmail, '✅ All checks passed - proceeding with analysis');

    try {
      // Step 5: Show immediate feedback
      toast.info('Starting AI Analysis...', { duration: 3000 });
      
      // Step 6: Clear previous errors
      clearAiError();

      // Step 7: Prepare analysis parameters (adapted for keyword research context)
      const dataFilter = {
        search_filter: currentQuery, // Use current query as filter
        data_types: [...new Set(data.map(k => k.data_type || activeTab))], // Get unique data types
        date_range: data.length > 0 ? {
          start: new Date(Math.min(...data.map(k => new Date(k.created_at).getTime()))).toISOString(),
          end: new Date(Math.max(...data.map(k => new Date(k.created_at).getTime()))).toISOString()
        } : undefined
      };

      const analysisParams = {
        workspace_id: workspaceId,
        workspace_name: workspaceName || `Workspace ${workspaceId.slice(-8)}`,
        data_filter: dataFilter,
        include_data: true,
        // Add context about the current search
        search_context: {
          query: currentQuery,
          language: currentLanguageName,
          location: currentCountryName,
          active_tab: activeTab
        }
      };

      debugLog(userEmail, '🛠 Analysis Parameters:', analysisParams);
      debugLog(userEmail, '🛠 Calling triggerAiAnalysis...');

      // Step 8: Trigger the analysis
      const response = await triggerAiAnalysis(analysisParams);

      debugLog(userEmail, '✅ AI Analysis Response:', response);
      toast.success('AI Analysis Started Successfully!');

    } catch (error: unknown) {
      const errorObj = error as Error;
      debugLog(userEmail, '❌ AI Analysis Error:', error);
      debugLog(userEmail, '❌ Error Details:', {
        name: errorObj.name,
        message: errorObj.message,
        stack: errorObj.stack
      });

      // Show user-friendly error
      const errorMessage = getAiErrorMessage();
      toast.error('AI Analysis Failed', {
        description: errorMessage || errorObj.message || 'Unknown error occurred'
      });
    }
    
    debugLog(userEmail, '🛠 =====================================');
    debugLog(userEmail, '🛠 AI ANALYSIS HANDLER COMPLETE');
    debugLog(userEmail, '🛠 =====================================');
  }, [
    currentQuery, 
    currentLanguageName, 
    currentCountryName, 
    workspaceId, 
    workspaceName, 
    activeTab, 
    data, 
    validatedUserId,
    isAiAnalysisLoading,
    triggerAiAnalysis,
    getAiErrorMessage,
    clearAiError,
    user
  ]);

  // Show notification when workspace is available and track polling state
  useEffect(() => {
    if (currentQuery && workspaceId && workspaceName) {
      // Always show workspace generation message when we have workspace info
      setNotification({
        type: 'info',
        title: 'Workspace Generated',
        message: `Your Workspace: ${workspaceName} has been generated`
      });
    } else if (connectionStatus === 'idle') {
      setNotification(null);
    }
  }, [workspaceId, workspaceName, currentQuery, connectionStatus]);

  // Real-time effect that triggers when we need to start/stop monitoring
  useEffect(() => {
    const searchParams = getSearchParams();
    
    if (currentQuery && validatedUserId && searchParams) {
      debugLog(user?.primaryEmailAddress?.emailAddress, `[KeywordTabs] Starting real-time monitoring for tab: ${activeTab}${workspaceId ? ` in workspace ${workspaceId}` : ''}`);
      startRealtime(searchParams);
    } else {
      debugLog(user?.primaryEmailAddress?.emailAddress, `[KeywordTabs] Clearing results and stopping real-time monitoring`);
      
      // Log why we're not searching
      if (!currentQuery) {
        debugLog(user?.primaryEmailAddress?.emailAddress, '[KeywordTabs] No query provided');
      }
      if (!validatedUserId) {
        debugLog(user?.primaryEmailAddress?.emailAddress, '[KeywordTabs] No valid user ID - search prevented');
      }
      
      clearResults();
    }

    // Cleanup real-time monitoring when effect dependencies change
    return () => {
      stopRealtime();
    };
  }, [currentQuery, validatedUserId, activeTab, workspaceId, user]); // Only depend on key values that should trigger new monitoring

  // Map frontend data types to database values (same as useSimpleKeywordQuery)
  const getDbDataType = (dataType: string): string => {
    const mapping: { [key: string]: string } = {
      'related': 'related_keywords',
      'ideas': 'keyword_ideas',
      'suggestions': 'keyword_suggestions',
      'subtopics': 'subtopics',
      'paa': 'people_also_ask',
      'trends': 'google_trends',
      'serp': 'serp_analysis'
    };
    return mapping[dataType] || dataType;
  };

  // Get available tabs (only show tabs that have data)
  const availableTabs = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return TAB_CONFIG.filter(tab => {
      // Handle consolidated Ideas & Suggestions tab
      if (tab.dataTypes) {
        const hasData = tab.dataTypes.some(dataType => 
          data.some(item => 
            item.data_type === getDbDataType(dataType) &&
            item.keyword && 
            item.keyword.trim().length > 0
          )
        );
        return hasData;
      }
      
      // Handle regular single dataType tabs - FIX: Use mapped database data type and check for meaningful content
      const dbDataType = getDbDataType(tab.dataType);
      
      // Check for meaningful content based on data type
      let hasData: boolean;
      if (tab.dataType === 'paa') {
        // For PAA tab, check that people_also_ask field has content
        hasData = data.some(item => 
          item.data_type === dbDataType && 
          item.people_also_ask && 
          item.people_also_ask.trim().length > 0
        );
      } else if (tab.dataType === 'subtopics') {
        // For subtopics tab, check that subtopic field has content
        hasData = data.some(item => 
          item.data_type === dbDataType && 
          item.subtopic && 
          item.subtopic.trim().length > 0
        );
      } else if (tab.dataType === 'serp') {
        // For SERP tab, check that url or title has content
        hasData = data.some(item => 
          item.data_type === dbDataType && 
          ((item.url && item.url.trim().length > 0) || (item.title && item.title.trim().length > 0))
        );
      } else {
        // For other tabs (related, ideas, suggestions, trends), check for keyword content
        hasData = data.some(item => 
          item.data_type === dbDataType && 
          item.keyword && 
          item.keyword.trim().length > 0
        );
      }
      
      // Debug logging for tab visibility issues
      if (tab.dataType === 'paa' || (process.env.NODE_ENV === 'development' && !hasData)) {
        const allMatchingItems = data.filter(item => item.data_type === dbDataType);
        const meaningfulItems = tab.dataType === 'paa' 
          ? data.filter(item => 
              item.data_type === dbDataType && 
              item.people_also_ask && 
              item.people_also_ask.trim().length > 0
            )
          : allMatchingItems;
          
        debugLog(user?.primaryEmailAddress?.emailAddress, `[TAB VISIBILITY DEBUG] ${tab.label} Tab Check:`, {
          tabId: tab.id,
          tabDataType: tab.dataType,
          dbDataType,
          hasData,
          dataTypesInResponse: [...new Set(data.map(item => item.data_type))],
          totalMatchingItems: allMatchingItems.length,
          meaningfulItems: meaningfulItems.length,
          emptyPAAItems: tab.dataType === 'paa' ? allMatchingItems.filter(item => !item.people_also_ask || item.people_also_ask.trim().length === 0).length : 0
        });
      }
      
      return hasData;
    });
  }, [data, user]);

  // Auto-select first available tab if current tab has no data
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(tab => tab.id === activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  // Get current tab data (AdvancedTable handles filtering and sorting internally)
  const currentTabData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const currentTabConfig = TAB_CONFIG.find(tab => tab.id === activeTab);
    if (!currentTabConfig) return [];
    
    // Handle consolidated Ideas & Suggestions tab
    if (currentTabConfig.dataTypes) {
      const mappedDataTypes = currentTabConfig.dataTypes.map(getDbDataType);
      return data.filter(item => 
        mappedDataTypes.includes(item.data_type) &&
        item.keyword && 
        item.keyword.trim().length > 0
      );
    }
    
    // Handle regular single dataType tabs - FIX: Use mapped database data type and filter out empty content
    const dbDataType = getDbDataType(currentTabConfig.dataType);
    
    // Filter based on data type and meaningful content
    if (currentTabConfig.dataType === 'paa') {
      return data.filter(item => 
        item.data_type === dbDataType && 
        item.people_also_ask && 
        item.people_also_ask.trim().length > 0
      );
    } else if (currentTabConfig.dataType === 'subtopics') {
      return data.filter(item => 
        item.data_type === dbDataType && 
        item.subtopic && 
        item.subtopic.trim().length > 0
      );
    } else if (currentTabConfig.dataType === 'serp') {
      return data.filter(item => 
        item.data_type === dbDataType && 
        ((item.url && item.url.trim().length > 0) || (item.title && item.title.trim().length > 0))
      );
    } else {
      // For other tabs (related, ideas, suggestions, trends), check for keyword content
      return data.filter(item => 
        item.data_type === dbDataType && 
        item.keyword && 
        item.keyword.trim().length > 0
      );
    }
  }, [data, activeTab]);

  return {
    // State
    activeTab,
    notification,
    data,
    isLoading,
    error,
    connectionStatus,
    validatedUserId,
    availableTabs,
    currentTabData,
    
    // AI Analysis
    isAiAnalysisLoading,
    aiAnalysisError,
    getAiErrorMessage,
    clearAiError,
    
    // Handlers
    handleTabChange,
    handleAiAnalysis,
    setNotification,
  };
};